import React from 'react'
import { Navigate, RouteObject } from 'react-router-dom'

// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Layouts
import AuthLayout from './layouts/Auth'
import DashboardLayout from './layouts/Dashboard'
import AdminDashboardLayout from './layouts/AdminDashboard'

// Auth components
import SignIn from './pages/auth/SignIn'
import ResetPassword from './pages/auth/ResetPassword'

// Page components

// Admin Components
import PRInput from './pages/admin/pr-input/PRInput'
import AdminIssuesView from './pages/admin/issues/AdminIssuesView'
import AdminIssueForm from './pages/admin/issues/issue-form/AdminIssueForm'
import AdminCustomersView from './pages/admin/customers/AdminCustomersView'
import AdminCustomerDetailsForm from './pages/admin/customers/AdminCustomerDetailsForm'
import AdminUsersView from './pages/admin/users/AdminUsersView'
import AdminCompaniesView from './pages/admin/companies/AdminCompaniesView'
import AdminCompanyForm from './pages/admin/companies/AdminCompanyForm'
import AdminConvertiblesView from './pages/admin/convertibles/AdminConvertiblesView'
import AdminDelistings from './pages/admin/delistings/AdminDelistings'
import AdminEditConvertible from './pages/admin/convertibles/edit-convertible/AdminEditConvertible'
import AdminNewConvertibleForm from './pages/admin/convertibles/new-convertible/AdminNewConvertibleForm'
import AdminWarrantsView from './pages/admin/warrants/AdminWarrantsView'
import AdminWarrantForm from './pages/admin/warrants/AdminWarrantForm'
import AdminInvestorsView from './pages/admin/investors/AdminInvestorsView'
import AdminInvestorDetails from './pages/admin/investors/AdminInvestorDetails'
import AdminKeywordFilterView from './pages/admin/keyword-filter/AdminKeywordFilterView'

// Dashboard components
import Overview from './pages/overview/Default'
import AdminDefault from './pages/admin/dashboard/Default'
import IssuesView from './pages/issues/IssuesView'
import IssueDetails from './pages/issues/IssueDetails'
import WarrantsView from './pages/warrants/WarrantsView'
import WarrantDetails from './pages/warrants/WarrantDetails'
import CompaniesView from './pages/companies/CompaniesView'
import CompanyDetails from './pages/companies/company/CompanyDetails'
import AdvisorsView from './pages/advisors/AdvisorsView'
import InvestorDetails from './pages/investors/InvestorDetails'
import NewsView from './pages/news/NewsView'
import AdvisorDetails from './pages/advisors/AdvisorDetails'
import Reports from './pages/reports/Reports'
import UserProfile from './pages/profile/UserProfile'
import ResetPasswordWithCode from './pages/auth/ResetPasswordWithCode'
import TestDates from './pages/admin/testdates/TestDates'
import { RouterError } from './components/RouterError.js'
import { findSubscriptionPeriodCloseToDate } from './utils/warrants.js'
import InvestorApi from './api/InvestorApi.js'
import { FallbackRoute } from './components/FallbackRoute'
import CompanyApi from './api/CompanyApi'
import IssueApi from './api/IssueApi'
import WarrantApi from './api/WarrantApi'
import AdminIssuesReview from './pages/admin/issues-review/AdminIssuesReview'
import StatisticsView from './pages/statistics/StatisticsView'
import CalendarView from './pages/calendar/CalendarView'
import { TrendsView } from './pages/statistics/TrendsView'
import { ScatterPlotView } from './pages/statistics/ScatterPlotView'
import { IssueMarketView } from './pages/statistics/issue-market/IssueMarketView'
import { WarrantEntity } from './types/WarrantEntity'
import { WatchListView } from './pages/watch-lists/WatchListsView'
import { PageNotFound } from './pages/page-not-found/PageNotFoundView'
import ConvertibleDetails from './pages/convertibles/convertible/ConvertibleDetails'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import ConvertiblesApi from './api/ConvertiblesApi'
import { CompanyEntity } from 'types/CompanyEntity'

type IssueIdParams = {
    issueId: string
}

type CompanyIdParams = {
    companyId: string
}

type WarrantIdParams = {
    warrantId: string
}

type ConvertibleIdParams = {
    convertibleId: string
}

const issueLoader = async ({ params }: { params: IssueIdParams }) => {
    const issueApi = new IssueApi()

    try {
        const result = await issueApi.findByIssueId(params.issueId)
        return result
    } catch (e) {
        if (e.error.response.status === 404) {
            throw new Response('Not Found', { status: 404 })
        }
    }
}

const cache: Record<string, CompanyEntity> = {}

const companyLoader = async ({ params }: { params: CompanyIdParams }) => {
    const companyApi = new CompanyApi()
    try {
        if (cache[params.companyId]) return cache[params.companyId]
        const company = await companyApi.findByCompanyId(params.companyId)

        if (company) {
            cache[params.companyId] = company

            setTimeout(
                () => {
                    delete cache[params.companyId]
                },
                60 * 60 * 1000,
            )
        }

        return company
    } catch (e) {
        if (e.error.response.status === 404) {
            throw new Response('Not Found', { status: 404 })
        }
    }
}

const warrantLoader = async ({ params }: { params: WarrantIdParams }) => {
    try {
        const warrantApi = new WarrantApi()
        const warrant = await warrantApi.findByWarrantId(params.warrantId)

        return warrant
    } catch (e) {
        if (e.error.response.status === 404) {
            throw new Response('Not Found', { status: 404 })
        }
    }
}

const convertibleLoader = async ({ params }: { params: ConvertibleIdParams }) => {
    try {
        const convertibleApi = new ConvertiblesApi()
        const convertible: ConvertibleEntity | null = await convertibleApi.findByConvertibleId(params.convertibleId)

        return convertible
    } catch (e) {
        if (e.error.response.status === 404) {
            throw new Response('Not Found', { status: 404 })
        }
    }
}

const investorLoader = async ({ params }) => {
    const api = new InvestorApi()
    try {
        return await api.getInvestor(params.investorId)
    } catch (e) {
        if (e.error.response.status === 404) {
            throw new Response('Not Found', { status: 404 })
        }
    }
}

export const authRoutes: RouteObject[] = [
    {
        path: '/admin',
        element: <AdminDashboardLayout />,
        children: [
            {
                path: 'dashboard',
                element: <AdminDefault />,
            },
            {
                path: 'customers',
                element: <AdminCustomersView />,
            },
            {
                path: 'customers/:customerId',
                element: <AdminCustomerDetailsForm />,
            },
            {
                path: 'users',
                element: <AdminUsersView />,
            },
            {
                path: 'pr-input',
                element: <PRInput />,
            },
            {
                path: 'delistings',
                element: <AdminDelistings />,
            },
            {
                path: 'issues',
                element: <AdminIssuesView />,
            },
            {
                path: 'issues/review',
                element: <AdminIssuesReview />,
            },
            {
                path: 'issues/:issueId',
                element: <AdminIssueForm />,
            },
            {
                path: 'companies',
                element: <AdminCompaniesView />,
            },
            {
                path: 'companies/:companyId',
                element: <AdminCompanyForm />,
            },
            {
                path: 'warrants',
                element: <AdminWarrantsView />,
            },
            {
                path: 'warrants/:warrantId',
                element: <AdminWarrantForm />,
            },
            {
                path: 'investors',
                element: <AdminInvestorsView />,
            },
            {
                path: 'investors/:investorId',
                element: <AdminInvestorDetails />,
            },
            {
                path: 'keyword-filter',
                element: <AdminKeywordFilterView />,
            },
            {
                path: 'convertibles',
                element: <AdminConvertiblesView />,
            },
            {
                path: 'convertibles/new',
                element: <AdminNewConvertibleForm />,
            },
            {
                path: 'convertibles/:convertibleId',
                element: <AdminEditConvertible />,
            },
            {
                path: 'test-dates',
                element: <TestDates />,
            },
        ],
    },
    {
        path: '/',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <Navigate to='/overview' replace />,
            },
        ],
    },
    {
        path: 'news',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <NewsView />,
            },
        ],
    },
    {
        path: 'calendar',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <CalendarView />,
            },
        ],
    },
    {
        path: '/overview',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <Overview />,
            },
        ],
    },
    {
        path: 'watch-lists',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <WatchListView />,
            },
        ],
    },
    {
        path: 'issues',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <IssuesView />,
            },
            {
                path: ':issueId',
                errorElement: <RouterError />,
                loader: issueLoader,
                element: <IssueDetails />,
            },
        ],
    },
    {
        path: 'statistics',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <StatisticsView />,
                children: [
                    {
                        path: 'trends',
                        element: <TrendsView />,
                    },
                    {
                        path: 'scatter-plots',
                        element: <ScatterPlotView />,
                    },
                    {
                        path: 'issue-market',
                        element: <IssueMarketView />,
                    },
                ],
            },
        ],
    },
    {
        path: 'warrants',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <WarrantsView />,
            },
            {
                path: ':warrantId',
                errorElement: <RouterError />,
                loader: warrantLoader,
                element: <WarrantDetails />,
            },
        ],
    },
    {
        path: 'convertibles',
        element: <DashboardLayout />,
        children: [
            {
                path: ':convertibleId',
                errorElement: <RouterError />,
                loader: convertibleLoader,
                element: <ConvertibleDetails />,
            },
        ],
    },
    {
        path: 'companies',
        element: <DashboardLayout />,
        shouldRevalidate: (args) => {
            return args.currentParams.companyId !== args.nextParams.companyId
        },
        children: [
            {
                path: '',
                element: <CompaniesView />,
            },
            {
                path: ':companyId',
                errorElement: <RouterError />,
                loader: companyLoader,
                element: <CompanyDetails />,
            },
        ],
    },
    {
        path: 'advisors',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <AdvisorsView />,
            },
            {
                path: ':advisorId',
                element: <AdvisorDetails />,
            },
        ],
    },
    {
        path: 'investors',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <InvestorDetails />,
            },
            {
                path: ':investorId',
                errorElement: <RouterError />,
                loader: investorLoader,
                element: <InvestorDetails />,
            },
        ],
    },
    {
        path: 'reports',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <Reports />,
            },
        ],
    },
    {
        path: 'profile',
        element: <DashboardLayout />,
        children: [
            {
                path: '',
                element: <UserProfile />,
            },
        ],
    },
    {
        path: '*',
        element: <DashboardLayout />,
        children: [
            {
                path: '404',
                element: <PageNotFound />,
            },
            {
                path: '*',
                element: <PageNotFound />,
            },
        ],
    },
]

export const anonymousRoutes = [
    {
        path: 'auth',
        element: <AuthLayout />,
        children: [
            {
                path: 'sign-in',
                element: <SignIn />,
            },
            {
                path: 'reset-password',
                element: <ResetPassword />,
            },
            {
                path: 'reset-password-code',
                element: <ResetPasswordWithCode />,
            },
        ],
    },
    {
        path: '*',
        element: <FallbackRoute />,
    },
]
